import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../store";
import { ApplicationDepartment } from "../../../models/organisation/application-department";
import DepartmentService from "../../../services/organisation/department-service";
import { ApplicationError } from "../../../models/errors/application-error";
import { ApplicationSubDepartmentList } from "../../../models/organisation/application-sub-department-list";
import { ApplicationDepartmentList } from "../../../models/organisation/application-department-list";
import { ApplicationSubDepartment } from "../../../models/organisation/application-sub-department";

interface DepartmentState {
  departments: ApplicationDepartmentList | null;
  subDepartments: ApplicationSubDepartmentList | null;
  loading: boolean;
  selectedDepartment: ApplicationDepartment | null;
  selectedSubDepartment: ApplicationSubDepartment | null;
  error: string | null;
}

const initialState: DepartmentState = {
  departments: null,
  subDepartments: null,
  loading: false,
  selectedDepartment: null,
  selectedSubDepartment: null,
  error: null,
};

export const fetchDepartments = createAsyncThunk<
  ApplicationDepartmentList,
  void,
  { state: RootState }
>("departments/fetch", async (_, { getState, rejectWithValue }) => {
  const departmentService = new DepartmentService();
  const state = getState();
  try {
    return await departmentService.get(0, -1, true);
  } catch (error: any) {
    const apiError = ApplicationError.handleApiError(error, {});
    return rejectWithValue(apiError);
  }
});

export const updateDepartment = createAsyncThunk<
  ApplicationDepartment,
  ApplicationDepartment,
  { state: RootState }
>("departments/update", async (department, { rejectWithValue }) => {
  const departmentService = new DepartmentService();
  try {
    const updatedDepartment = await departmentService.updateDepartment(
      department
    );
    return updatedDepartment;
  } catch (error: any) {
    const apiError = ApplicationError.handleApiError(error, {});
    return rejectWithValue(apiError);
  }
});


export const fetchSubDepartments = createAsyncThunk<
  ApplicationSubDepartmentList | null,
  number,
  { state: RootState }
>(
  "products/department/subDepartments/fetch",
  async (departmentId, { rejectWithValue }) => {
    const departmentService = new DepartmentService();
    try {
      return await departmentService.getSubDepartments(departmentId, 0, -1, true);
    } catch (error: any) {
      const apiError = ApplicationError.handleApiError(error, {});
      return rejectWithValue(apiError);
    }
  }
);

export const fetchSubDepartment = createAsyncThunk<
  ApplicationSubDepartment,
  number,
  { state: RootState }
>(
  "products/department/subDepartment/fetchSingle",
  async (subDepartmentId, { rejectWithValue }) => {
    const departmentService = new DepartmentService();
    try {
      return await departmentService.getSubDepartmentById(subDepartmentId);
    } catch (error: any) {
      const apiError = ApplicationError.handleApiError(error, {});
      return rejectWithValue(apiError);
    }
  }
);

const departmentSlice = createSlice({
  name: "department",
  initialState,
  reducers: {
    setSelectedDepartment: (
      state,
      action: PayloadAction<ApplicationDepartment | null>
    ) => {
      state.selectedDepartment = action.payload;
      state.subDepartments = initialState.subDepartments;
    },
    removeDepartment: (state, action: PayloadAction<number>) => {
      if (state.departments) {
        state.departments.departments =
          state.departments?.departments.filter(
            (department) => department.id !== action.payload
          ) || [];
      }
    },
    addDepartment: (state, action: PayloadAction<ApplicationDepartment>) => {
      state.departments?.departments.push(action.payload);
      state.departments!.count += 1;
      state.departments!.currentPosition += 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDepartments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchDepartments.fulfilled,
        (state, action: PayloadAction<ApplicationDepartmentList>) => {
          state.departments = action.payload;
          state.loading = false;
        }
      )
      .addCase(
        updateDepartment.fulfilled,
        (state, action: PayloadAction<ApplicationDepartment>) => {
          if (state.departments) {
            const index = state.departments.departments.findIndex(item => item.id === action.payload.id);
            if (index !== -1) {
              state.departments.departments[index] = action.payload;
            }
          }
        }
      )
      .addCase(
        fetchDepartments.rejected,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      )
      .addCase(
        fetchSubDepartments.fulfilled,
        (
          state,
          action: PayloadAction<ApplicationSubDepartmentList | null>
        ) => {
          if (action.payload) {
            state.subDepartments = action.payload;
          }
        }
      )
      .addCase(
        fetchSubDepartment.fulfilled,
        (
          state,
          action: PayloadAction<ApplicationSubDepartment>
        ) => {
          if (action.payload) {
            state.selectedSubDepartment = action.payload;
          }
        }
      );
  },
});

export const { setSelectedDepartment, removeDepartment, addDepartment } =
  departmentSlice.actions;

export default departmentSlice.reducer;
